import moment from "moment";
import { PickupLogItem } from "../../../app/data/pickup/models";
import { dateFormats } from "../../../app/data/common/dateFormats";

/**
 * Interface for data passed to formatAdditionalInfo function
 * This is a subset of PickupLogItem with only the fields needed for formatting
 */
export interface AdditionalInfoData {
  reason?: string | null;
  notes?: string | null;
  actionPerformed?: string | null;
  oldPickupDate?: string | null;
  oldPickupReadyTime?: string | null;
  oldPickupCloseTime?: string | null;
}

/**
 * Helper function to format time from "06:00:00" to "6:00 am" format
 * @param time - Time string in "HH:MM:SS" or "HH:MM" format
 * @returns Formatted time string in "H:MM am/pm" format, or empty string if time is invalid
 */
export const formatTime = (time: string | null | undefined): string => {
  if (!time) return '';
  
  try {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const minute = parseInt(minutes, 10);
    const period = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = `:${minute.toString().padStart(2, '0')}`;
    return `${displayHour}${displayMinute} ${period}`;
  } catch {
    return time;
  }
};

/**
 * Helper function to format additional info based on action and new backend fields
 * @param data - Pickup log item data containing reason, notes, action, and old pickup details
 * @returns Object with formatted reason and formatted text
 */
export const formatAdditionalInfo = (data: AdditionalInfoData | PickupLogItem): { reason: string | null; formattedText: string | null } => {
  const reason = data.reason || null;
  const notes = data.notes || null;
  
  // If no reason and no notes, return blank
  if (!reason && !notes) {
    return { reason: null, formattedText: null };
  }
  
  // If no reason but has notes, just return notes
  if (!reason && notes) {
    return { reason: null, formattedText: notes };
  }
  
  const reasonLabel = `Reason: ${reason}`;
  
  // For RESCHEDULE_PICKUP
  if (data.actionPerformed === "RESCHEDULE_PICKUP") {
    const parts: string[] = [reasonLabel];
    
    if (data.oldPickupDate) {
      // Format date as YYYY-MM-DD using moment for robust parsing
      let formattedDate: string;
      try {
        // Try parsing as ISO date string (with or without time)
        const date = moment(data.oldPickupDate);
        if (date.isValid()) {
          formattedDate = date.format(dateFormats.apiDate);
        } else {
          // Fallback to string splitting if moment parsing fails
          formattedDate = data.oldPickupDate.split('T')[0];
        }
      } catch {
        // Fallback to string splitting if moment parsing fails
        formattedDate = data.oldPickupDate.split('T')[0];
      }
      
      const readyTime = formatTime(data.oldPickupReadyTime);
      const closeTime = formatTime(data.oldPickupCloseTime);
      
      parts.push(`Old Schedule — Date: ${formattedDate}, Ready Time: ${readyTime}, Close Time: ${closeTime}.`);
    }
    
    if (notes) {
      parts.push(`Notes: ${notes}`);
    }
    
    return {
      reason: reasonLabel,
      formattedText: parts.join(', ')
    };
  }
  
  // For CANCELED
  if (data.actionPerformed === "CANCELED") {
    const parts: string[] = [reasonLabel];
    
    if (notes) {
      parts.push(`Notes: ${notes}`);
    }
    
    return {
      reason: reasonLabel,
      formattedText: parts.join(', ')
    };
  }
  
  // For other actions, return reason and notes
  return {
    reason: reasonLabel,
    formattedText: notes ? `${reasonLabel}, Notes: ${notes}` : reasonLabel
  };
};

/**
 * Helper function to extract remaining text after the reason label
 * This removes the reason prefix and any leading comma/space from the formatted text
 * @param formattedText - The full formatted text string
 * @param reason - The reason label to remove from the beginning
 * @returns The remaining text after the reason label, with leading comma/space removed
 */
export const extractRemainingTextAfterReason = (formattedText: string | null, reason: string | null): string => {
  if (!formattedText || !reason) {
    return formattedText || '';
  }
  
  // Extract text after the reason label
  const afterReason = formattedText.substring(reason.length).trim();
  
  // Remove leading comma and space if present
  const remainingText = afterReason.startsWith(',') ? afterReason.substring(1).trim() : afterReason;
  
  return remainingText;
};


export const getTomorrowAndTomorrowPlus7Days = () => {
  // Tomorrow's date
  const tomorrow = moment().add(1, 'days').format('MM/DD/YYYY');

  // Tomorrow + 7 days
  const tomorrowPlus7Days = moment().add(8, 'days').format('MM/DD/YYYY');

  return { tomorrow, tomorrowPlus7Days };
};

export const isFuture = (dateToCheck: string) => {
  return moment(dateToCheck, 'MM/DD/YYYY').isAfter(moment());
};

export const getPluralOrSingular = (count: number, singular: string, plural: string) => {
  return count === 1 ? singular : plural;
}